/**
 * Correspondence Inbox — portfolio-wide inbox of every contract correspondence.
 * Features: time-bar / SLA traffic-lights, response-pending filter, Cognara reply drafter, Resend email send.
 */
import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Mail, Search, AlertTriangle, Clock, CheckCircle2, Sparkles, Send, Loader2, ArrowLeft } from 'lucide-react';
import api from '@/lib/api';

const SLA = {
  BREACHED: { bg: '#EF444422', color: '#F87171', icon: AlertTriangle },
  URGENT:   { bg: '#F9731622', color: '#FB923C', icon: AlertTriangle },
  WARN:     { bg: '#F59E0B22', color: '#FBBF24', icon: Clock },
  OK:       { bg: '#22C55E22', color: '#4ADE80', icon: CheckCircle2 },
};

export default function ContractsCorrespondence() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [slaFilter, setSlaFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const [showDrafter, setShowDrafter] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [draft, setDraft] = useState({ subject: '', body: '', to: '' });
  const [sending, setSending] = useState(false);

  useEffect(() => { (async () => {
    try { const r = await api.get('/contracts/portfolio/correspondences'); setData(r.data); }
    catch { toast.error('Failed to load correspondences'); } finally { setLoading(false); }
  })(); }, []);

  const rows = data?.correspondences || [];
  const filtered = useMemo(() => rows.filter(r => {
    const t = q.toLowerCase();
    const okQ = !t || (r.subject || '').toLowerCase().includes(t) || (r.reference_number || '').toLowerCase().includes(t) || (r.project || '').toLowerCase().includes(t);
    const okS = slaFilter === 'all' || r.sla_status === slaFilter;
    const okT = typeFilter === 'all' || r.type === typeFilter;
    return okQ && okS && okT;
  }), [rows, q, slaFilter, typeFilter]);
  const types = useMemo(() => Array.from(new Set(rows.map(r => r.type).filter(Boolean))), [rows]);
  const selected = rows.find(r => r.id === selectedId);

  const draftReply = async () => {
    if (!selected) return;
    setDrafting(true); setShowDrafter(true);
    try {
      const r = await api.post(`/contracts/correspondences/${selected.id}/draft-reply`);
      setDraft(d => ({ ...d, subject: r.data.subject || `Re: ${selected.subject}`, body: r.data.body || '' }));
      toast.success('Cognara draft ready');
    } catch { toast.error('Draft generation failed'); }
    finally { setDrafting(false); }
  };

  const sendReply = async () => {
    if (!selected || !draft.to) return toast.error('Add recipient email');
    setSending(true);
    try {
      await api.post(`/contracts/correspondences/${selected.id}/send-reply`, { to: draft.to.split(',').map(s => s.trim()), subject: draft.subject, html: draft.body.replace(/\n/g, '<br/>') });
      toast.success('Reply sent'); setShowDrafter(false);
      const r = await api.get('/contracts/portfolio/correspondences'); setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Send failed'); }
    finally { setSending(false); }
  };

  const c = data?.counts || {};

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="contracts-correspondence">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/contracts')} data-testid="corr-back-btn"><ArrowLeft className="w-4 h-4" /></Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Correspondence Inbox</h1>
            <p className="text-sm text-muted-foreground mt-1">Every correspondence · time-bar tracker · SLA breach alerts · Cognara reply drafter</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="corr-kpis">
          {[
            { label: 'Total', value: c.total, color: '#94A3B8' },
            { label: 'SLA Breached', value: c.breached, color: '#EF4444' },
            { label: 'Urgent (≤3d)', value: c.urgent, color: '#F97316' },
            { label: 'Response Pending', value: c.response_pending, color: '#F59E0B' },
          ].map(kpi => (
            <Card key={kpi.label} className="bg-card border-border"><CardContent className="p-3">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{kpi.label}</p>
              <p className="text-2xl font-bold" style={{ color: kpi.color }}>{kpi.value || 0}</p>
            </CardContent></Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-4">
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search subject / ref / project..." className="pl-10 bg-muted border-border text-foreground" data-testid="corr-search" />
                </div>
                <Select value={slaFilter} onValueChange={setSlaFilter}>
                  <SelectTrigger className="w-40 bg-muted border-border text-foreground" data-testid="corr-sla-filter"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {['all', 'BREACHED', 'URGENT', 'WARN', 'OK'].map(s => <SelectItem key={s} value={s}>{s === 'all' ? 'All SLA' : s}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-40 bg-muted border-border text-foreground" data-testid="corr-type-filter"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="all">All Types</SelectItem>
                    {types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="max-h-[600px] overflow-y-auto space-y-1" data-testid="corr-list">
                {filtered.map(r => {
                  const s = SLA[r.sla_status] || SLA.OK; const Icon = s.icon;
                  return (
                    <div key={r.id} onClick={() => setSelectedId(r.id)} className={`p-3 rounded cursor-pointer border ${selectedId === r.id ? 'bg-white/[0.05] border-purple-500/50' : 'border-transparent hover:bg-white/[0.02]'}`} data-testid={`corr-row-${r.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="text-[9px] font-mono">{r.reference_number}</Badge>
                            <Badge variant="outline" className="text-[9px]">{r.type}</Badge>
                            {r.is_time_barred && <Badge style={{ background: s.bg, color: s.color }} className="text-[9px] flex items-center gap-1"><Icon className="w-2.5 h-2.5" />{r.sla_status}{r.days_left != null ? ` · ${r.days_left}d` : ''}</Badge>}
                          </div>
                          <p className="text-sm text-foreground truncate">{r.subject}</p>
                          <p className="text-[10px] text-muted-foreground truncate">{r.project} · {r.from} → {r.to}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {filtered.length === 0 && <p className="text-muted-foreground text-sm text-center py-8">No correspondences match filters</p>}
              </div>
            </CardContent>
          </Card>

          {/* Detail pane */}
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              {selected ? (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-foreground">{selected.reference_number}</h3>
                    <Badge variant="outline" className="text-[10px]">{selected.status}</Badge>
                  </div>
                  <p className="text-lg font-semibold text-foreground mb-2">{selected.subject}</p>
                  <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                    <div><span className="text-muted-foreground">From:</span> <span className="text-foreground/80">{selected.from}</span></div>
                    <div><span className="text-muted-foreground">To:</span> <span className="text-foreground/80">{selected.to}</span></div>
                    <div><span className="text-muted-foreground">Type:</span> <span className="text-foreground/80">{selected.type}</span></div>
                    <div><span className="text-muted-foreground">Project:</span> <span className="text-foreground/80">{selected.project}</span></div>
                    {selected.is_time_barred && <>
                      <div><span className="text-muted-foreground">Time-bar:</span> <span className="text-foreground/80">{selected.time_bar_clause}</span></div>
                      <div><span className="text-muted-foreground">Deadline:</span> <span style={{ color: SLA[selected.sla_status]?.color }}>{selected.time_bar_deadline?.slice(0, 10)}</span></div>
                    </>}
                  </div>
                  <div className="space-y-2">
                    <Button onClick={draftReply} className="w-full bg-purple-600 hover:bg-purple-700 text-foreground" size="sm" data-testid="corr-draft-btn">
                      <Sparkles className="w-3.5 h-3.5 mr-1" />Draft Reply with Cognara
                    </Button>
                    <Button variant="outline" onClick={() => navigate(`/contracts/${selected.contract_id}`)} className="w-full border-border text-foreground/80" size="sm" data-testid="corr-open-contract-btn">
                      Open Contract → {selected.contract_number}
                    </Button>
                  </div>
                </>
              ) : <p className="text-muted-foreground text-sm py-8 text-center">Select a correspondence to view details</p>}
            </CardContent>
          </Card>
        </div>

        <Dialog open={showDrafter} onOpenChange={setShowDrafter}>
          <DialogContent className="max-w-2xl bg-card border-border text-foreground" data-testid="corr-drafter-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-purple-400" />Cognara Reply Drafter</DialogTitle>
              <DialogDescription>Review and edit before sending via Resend.</DialogDescription>
            </DialogHeader>
            {drafting ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-purple-500 mr-2" /><span className="text-sm text-muted-foreground">Cognara is drafting...</span></div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-muted-foreground">To (comma-separated)</label>
                  <Input value={draft.to} onChange={e => setDraft(d => ({ ...d, to: e.target.value }))} placeholder="counterparty@example.com" className="bg-muted border-border" data-testid="corr-to-input" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Subject</label>
                  <Input value={draft.subject} onChange={e => setDraft(d => ({ ...d, subject: e.target.value }))} className="bg-muted border-border" data-testid="corr-subject-input" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Body</label>
                  <Textarea value={draft.body} onChange={e => setDraft(d => ({ ...d, body: e.target.value }))} className="bg-muted border-border min-h-[220px]" data-testid="corr-body-input" />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDrafter(false)} className="border-border">Cancel</Button>
              <Button onClick={sendReply} disabled={sending || drafting} className="bg-emerald-600 hover:bg-emerald-700 text-foreground" data-testid="corr-send-btn">
                {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Send className="w-3.5 h-3.5 mr-1" />}Send via Resend
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
